import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Circle,
  Plus,
  Trash2,
  Calendar,
  DollarSign,
  ListTodo,
  TrendingUp,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultChecklist = [
  { title: 'Book venue', category: 'Venue', due_date: '' },
  { title: 'Hire photographer', category: 'Photography', due_date: '' },
  { title: 'Book makeup artist', category: 'Beauty', due_date: '' },
  { title: 'Send save the dates', category: 'Invitations', due_date: '' },
  { title: 'Book caterer', category: 'Food', due_date: '' },
  { title: 'Order wedding attire', category: 'Attire', due_date: '' },
  { title: 'Book decorator', category: 'Decor', due_date: '' },
  { title: 'Hire DJ/Music', category: 'Entertainment', due_date: '' },
  { title: 'Book mehndi artist', category: 'Beauty', due_date: '' },
  { title: 'Finalize guest list', category: 'Planning', due_date: '' },
];

const budgetCategories = [
  'Venue',
  'Photography',
  'Catering',
  'Decor',
  'Attire',
  'Entertainment',
  'Beauty',
  'Invitations',
  'Transportation',
  'Other',
];

const PlanningPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('checklist');
  const [checklist, setChecklist] = useState([]);
  const [budget, setBudget] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newItemOpen, setNewItemOpen] = useState(false);
  const [newBudgetOpen, setNewBudgetOpen] = useState(false);

  // Form states
  const [newTask, setNewTask] = useState({ title: '', category: '', due_date: '' });
  const [newBudgetItem, setNewBudgetItem] = useState({ 
    category: '', 
    item_name: '', 
    estimated_cost: '', 
    actual_cost: '' 
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [checklistRes, budgetRes] = await Promise.all([
        axios.get(`${API}/checklist`, { headers: getAuthHeader() }),
        axios.get(`${API}/budget`, { headers: getAuthHeader() }),
      ]);
      setChecklist(checklistRes.data || []);
      setBudget(budgetRes.data || []);
    } catch (error) {
      toast.error('Failed to load planning data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (itemId, isCompleted) => {
    try {
      await axios.put(`${API}/checklist/${itemId}?is_completed=${!isCompleted}`, {}, { 
        headers: getAuthHeader() 
      });
      setChecklist(checklist.map(item => 
        item.id === itemId ? { ...item, is_completed: !isCompleted } : item
      ));
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleAddTask = async () => {
    if (!newTask.title) return;
    try {
      const response = await axios.post(`${API}/checklist`, newTask, { 
        headers: getAuthHeader() 
      });
      setChecklist([...checklist, response.data]);
      setNewTask({ title: '', category: '', due_date: '' });
      setNewItemOpen(false);
      toast.success('Task added!');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleDeleteTask = async (itemId) => {
    try {
      await axios.delete(`${API}/checklist/${itemId}`, { headers: getAuthHeader() });
      setChecklist(checklist.filter(item => item.id !== itemId));
      toast.success('Task deleted');
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleAddBudgetItem = async () => {
    if (!newBudgetItem.item_name || !newBudgetItem.estimated_cost) return;
    try {
      const response = await axios.post(`${API}/budget`, {
        ...newBudgetItem,
        estimated_cost: parseFloat(newBudgetItem.estimated_cost),
        actual_cost: newBudgetItem.actual_cost ? parseFloat(newBudgetItem.actual_cost) : null,
      }, { headers: getAuthHeader() });
      setBudget([...budget, response.data]);
      setNewBudgetItem({ category: '', item_name: '', estimated_cost: '', actual_cost: '' });
      setNewBudgetOpen(false);
      toast.success('Budget item added!');
    } catch (error) {
      toast.error('Failed to add budget item');
    }
  };

  const handleDeleteBudgetItem = async (itemId) => {
    try {
      await axios.delete(`${API}/budget/${itemId}`, { headers: getAuthHeader() });
      setBudget(budget.filter(item => item.id !== itemId));
      toast.success('Budget item deleted');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const initializeChecklist = async () => {
    try {
      for (const item of defaultChecklist) {
        await axios.post(`${API}/checklist`, item, { headers: getAuthHeader() });
      }
      fetchData();
      toast.success('Checklist initialized!');
    } catch (error) {
      toast.error('Failed to initialize checklist');
    }
  };

  // Calculate stats
  const completedTasks = checklist.filter(t => t.is_completed).length;
  const totalTasks = checklist.length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const totalEstimated = budget.reduce((sum, item) => sum + (item.estimated_cost || 0), 0);
  const totalActual = budget.reduce((sum, item) => sum + (item.actual_cost || 0), 0);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-pink-500" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-4">Start Planning Your Dream Wedding</h2>
          <p className="text-muted-foreground mb-6">
            Create an account to access your personalized checklist, budget tracker, and more.
          </p>
          <Button onClick={() => navigate('/register')} className="btn-primary" data-testid="planning-register-btn">
            Get Started Free
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-pink-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold mb-2">
            Planning <span className="gradient-text">Tools</span>
          </h1>
          <p className="text-muted-foreground">Keep your wedding planning on track</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-6 border border-pink-100"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-pink-100 flex items-center justify-center">
                <ListTodo className="w-6 h-6 text-pink-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tasks Completed</p>
                <p className="text-2xl font-bold">{completedTasks} / {totalTasks}</p>
              </div>
            </div>
            <Progress value={progress} className="h-2" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl p-6 border border-pink-100"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Estimated Budget</p>
                <p className="text-2xl font-bold">${totalEstimated.toLocaleString()}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-6 border border-pink-100"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Actual Spent</p>
                <p className="text-2xl font-bold">${totalActual.toLocaleString()}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          <Button
            onClick={() => setActiveTab('checklist')}
            variant={activeTab === 'checklist' ? 'default' : 'outline'}
            className={`rounded-full ${activeTab === 'checklist' ? 'bg-primary' : 'border-pink-200'}`}
            data-testid="tab-checklist"
          >
            <ListTodo className="w-4 h-4 mr-2" />
            Checklist
          </Button>
          <Button
            onClick={() => setActiveTab('budget')}
            variant={activeTab === 'budget' ? 'default' : 'outline'}
            className={`rounded-full ${activeTab === 'budget' ? 'bg-primary' : 'border-pink-200'}`}
            data-testid="tab-budget"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Budget
          </Button>
        </div>

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-3xl p-6 lg:p-8 border border-pink-100"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl">Wedding Checklist</h2>
              <div className="flex gap-2">
                {checklist.length === 0 && (
                  <Button 
                    variant="outline" 
                    onClick={initializeChecklist}
                    className="rounded-full border-pink-200"
                  >
                    Use Template
                  </Button>
                )}
                <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary gap-2" data-testid="add-task-btn">
                      <Plus className="w-4 h-4" />
                      Add Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-3xl">
                    <DialogHeader>
                      <DialogTitle>Add New Task</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <Input
                        placeholder="Task name"
                        value={newTask.title}
                        onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                        className="rounded-xl"
                      />
                      <Select 
                        value={newTask.category} 
                        onValueChange={(v) => setNewTask({ ...newTask, category: v })}
                      >
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetCategories.map((cat) => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="date"
                        value={newTask.due_date}
                        onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                        className="rounded-xl"
                      />
                      <Button onClick={handleAddTask} className="w-full btn-primary">
                        Add Task
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={`checklist-skel-${i}`} className="h-16 bg-pink-50 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : checklist.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ListTodo className="w-8 h-8 text-pink-300" />
                </div>
                <p className="text-muted-foreground">No tasks yet. Add your first task or use our template!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {checklist.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                      item.is_completed 
                        ? 'bg-green-50 border-green-100' 
                        : 'bg-white border-pink-100 hover:border-pink-200'
                    }`}
                    data-testid={`checklist-item-${item.id}`}
                  >
                    <button
                      onClick={() => handleToggleTask(item.id, item.is_completed)}
                      className="flex-shrink-0"
                    >
                      {item.is_completed ? (
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                      ) : (
                        <Circle className="w-6 h-6 text-pink-300" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${item.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {item.category && <span>{item.category}</span>}
                        {item.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(item.due_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteTask(item.id)}
                      className="text-red-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Budget Tab */}
        {activeTab === 'budget' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-3xl p-6 lg:p-8 border border-pink-100"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl">Budget Tracker</h2>
              <Dialog open={newBudgetOpen} onOpenChange={setNewBudgetOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary gap-2" data-testid="add-budget-btn">
                    <Plus className="w-4 h-4" />
                    Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle>Add Budget Item</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <Select 
                      value={newBudgetItem.category} 
                      onValueChange={(v) => setNewBudgetItem({ ...newBudgetItem, category: v })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {budgetCategories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Item name (e.g., DJ deposit)"
                      value={newBudgetItem.item_name}
                      onChange={(e) => setNewBudgetItem({ ...newBudgetItem, item_name: e.target.value })}
                      className="rounded-xl"
                    />
                    <Input
                      type="number"
                      placeholder="Estimated cost"
                      value={newBudgetItem.estimated_cost}
                      onChange={(e) => setNewBudgetItem({ ...newBudgetItem, estimated_cost: e.target.value })}
                      className="rounded-xl"
                    />
                    <Input
                      type="number"
                      placeholder="Actual cost (optional)"
                      value={newBudgetItem.actual_cost}
                      onChange={(e) => setNewBudgetItem({ ...newBudgetItem, actual_cost: e.target.value })}
                      className="rounded-xl"
                    />
                    <Button onClick={handleAddBudgetItem} className="w-full btn-primary">
                      Add Item
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={`budget-skel-${i}`} className="h-16 bg-pink-50 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : budget.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-pink-300" />
                </div>
                <p className="text-muted-foreground">No budget items yet. Start tracking your expenses!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-pink-100">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Category</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Item</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Estimated</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actual</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {budget.map((item) => (
                      <tr key={item.id} className="border-b border-pink-50 hover:bg-pink-50/50" data-testid={`budget-item-${item.id}`}>
                        <td className="py-3 px-4">{item.category}</td>
                        <td className="py-3 px-4 font-medium">{item.item_name}</td>
                        <td className="py-3 px-4 text-right">${item.estimated_cost?.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          {item.actual_cost ? `$${item.actual_cost.toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => handleDeleteBudgetItem(item.id)}
                            className="text-red-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-pink-50 font-bold">
                      <td className="py-3 px-4" colSpan={2}>Total</td>
                      <td className="py-3 px-4 text-right">${totalEstimated.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right">${totalActual.toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlanningPage;
